import { useTranslation } from 'react-i18next'
import { mdiLoading } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { AppIcon } from '../ui/icon'
import { EmptyState } from '../ui/empty-state'
import { SECTION_ICONS } from '../../lib/section-icons'

/**
 * The organization rules proposed for the shared catalogue (spec 2026-10-27-21
 * § 5), across organizations. Promotion is a seed change under a shipped
 * code; this list is what the team reviews.
 */
export function ProposedRulesList() {
    const { t, i18n } = useTranslation('admin')
    const { data, isLoading } = $api.useQuery('get', '/api/v1/admin/proposed-rules', {})
    const rules = data?.data ?? []
    if (isLoading) {
        return (
            <div className="flex justify-center py-12">
                <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
            </div>
        )
    }
    if (rules.length === 0) {
        return <EmptyState icon={SECTION_ICONS.businessRules} title={t('proposed_rules.empty')} data-testid="proposed-rules-empty" />
    }
    return (
        <div className="overflow-x-auto rounded-lg border" data-testid="proposed-rules-list">
            <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2">{t('proposed_rules.rule')}</th>
                        <th className="px-3 py-2">{t('proposed_rules.organization')}</th>
                        <th className="px-3 py-2">{t('proposed_rules.control')}</th>
                        <th className="px-3 py-2">{t('proposed_rules.definition')}</th>
                        <th className="px-3 py-2 text-right">{t('proposed_rules.applicable')}</th>
                        <th className="px-3 py-2">{t('proposed_rules.proposed_at')}</th>
                    </tr>
                </thead>
                <tbody className="divide-y">
                    {rules.map((r) => {
                        const label = (r.label as Record<string, string> | undefined) ?? {}
                        return (
                            <tr key={r.uid} data-testid={`proposed-rule-${r.uid}`}>
                                <td className="px-3 py-2">
                                    <div>{label[i18n.language] || label.fr || label.en || r.code}</div>
                                    <div className="font-mono text-[11px] text-muted-foreground">{r.code}</div>
                                </td>
                                <td className="px-3 py-2">{r.organization_name} <span className="text-muted-foreground">({r.organization_code})</span></td>
                                <td className="px-3 py-2 font-mono text-xs">{r.control_code}</td>
                                <td className="max-w-md px-3 py-2">
                                    <code className="block truncate text-[11px] text-muted-foreground" title={JSON.stringify(r.check)}>
                                        {JSON.stringify(r.check)}
                                    </code>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{r.applicable_assets}</td>
                                <td className="px-3 py-2 text-xs text-muted-foreground">{r.proposed_at ? new Date(r.proposed_at).toLocaleDateString(i18n.language) : ''}</td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}
